import { DeleteDocument } from "./DeleteDocument";
import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  COMMERCIAL_DOCUMENT_LABELS,
  CommercialDocumentInputSchema,
  type CommercialDocument,
  type CommercialDocumentFiling,
  type CommercialDocumentInput,
} from "../../shared/commercial-documents";
import { ArrowLeft, Copy, FileCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { CommercialDocumentFields } from "./CommercialDocumentFields";
import { FiledNotice, FileSignedDialog } from "./CommercialDocumentFiling";
import { CommercialDocumentHistory } from "./CommercialDocumentHistory";
import { CommercialDocumentPreview } from "./CommercialDocumentPreview";
import { saveCommercialDocument } from "./commercial-documents-api";
import { downloadCommercialDocument } from "./download-commercial-document";

type EditorProps = {
  slug: string;
  id: string;
  initial: CommercialDocumentInput;
  current?: CommercialDocument;
  filing?: CommercialDocumentFiling | null;
  canWrite: boolean;
  onClose: () => void;
  onSaved: (record: CommercialDocument) => void;
  onFiled: (record: CommercialDocument, filing: CommercialDocumentFiling) => void;
  onDuplicate: (record: CommercialDocument) => void;
};

function validateDraft(draft: CommercialDocumentInput, onValidation: (messages: string[]) => void) {
  const parsed = CommercialDocumentInputSchema.safeParse(draft);
  if (!parsed.success) {
    onValidation(parsed.error.issues.map((issue) => `${issue.path.join(" · ")}: ${issue.message}`));
    throw new Error("Check the document details below before saving or downloading.");
  }
  onValidation([]);
  return parsed.data;
}
function useEditor(props: EditorProps) {
  const [draft, setDraft] = useState(props.initial);
  const [record, setRecord] = useState(props.current);
  const [filing, setFiling] = useState(props.filing ?? null);
  const [filingOpen, setFilingOpen] = useState(false);
  const [changeNote, setChangeNote] = useState("");
  const [historyBusy, setHistoryBusy] = useState(false);
  // A filed document is the signed record: it stays readable and downloadable, never editable.
  const editable = props.canWrite && !filing;
  const [tab, setTab] = useState<"edit" | "preview" | "history">(editable ? "edit" : "preview");
  const [validation, setValidation] = useState<string[]>([]);
  const dirty =
    JSON.stringify(draft) !== JSON.stringify(record?.document ?? props.initial) ||
    changeNote.length > 0;
  const saved = (next: CommercialDocument) => {
    setRecord(next);
    setDraft(next.document);
    setChangeNote("");
    setValidation([]);
    props.onSaved(next);
  };
  const save = useMutation({
    mutationFn: async (withPdf: boolean) => {
      const document = validateDraft(draft, setValidation);
      const response = await saveCommercialDocument(props.slug, props.id, {
        document,
        expectedRevision: record?.revision ?? 0,
        changeNote,
      });
      saved(response.document);
      if (withPdf) await downloadCommercialDocument(response.document);
    },
  });
  const download = useMutation({
    mutationFn: () =>
      downloadCommercialDocument(
        record && !dirty
          ? record
          : { document: validateDraft(draft, setValidation), revision: null },
      ),
  });
  const busy = save.isPending || download.isPending || historyBusy;
  const filed = (signed: CommercialDocument, next: CommercialDocumentFiling) => {
    setFiling(next);
    setFilingOpen(false);
    setTab("preview");
    props.onFiled(signed, next);
  };
  return {
    filing,
    filed,
    filingOpen,
    setFilingOpen,
    editable,
    draft,
    setDraft,
    record,
    changeNote,
    setChangeNote,
    tab,
    setTab,
    validation,
    saved,
    save,
    download,
    busy,
    dirty,
    setHistoryBusy,
  };
}
function useLeaveGuard(dirty: boolean, busy: boolean) {
  const attach = useCallback(() => {
    const unload = (event: BeforeUnloadEvent) => {
      if (dirty || busy) event.preventDefault();
    };
    const navigate = (event: Event) => {
      if (
        busy ||
        (dirty && !window.confirm("Your changes have not been saved. Leave this document?"))
      )
        event.preventDefault();
    };
    window.addEventListener("beforeunload", unload);
    window.addEventListener("das:operations:before-navigate", navigate);
    return () => {
      window.removeEventListener("beforeunload", unload);
      window.removeEventListener("das:operations:before-navigate", navigate);
    };
  }, [dirty, busy]);
  return attach;
}
type EditorState = ReturnType<typeof useEditor>;

export function CommercialDocumentEditor(props: EditorProps) {
  const state = useEditor(props);
  const { draft, record } = state;
  const attach = useLeaveGuard(state.dirty, state.busy);
  return (
    <section ref={attach} aria-label="Document editor" className="space-y-5">
      <header className="space-y-3">
        <Button variant="ghost" disabled={state.busy} onClick={props.onClose}>
          <ArrowLeft /> Back to documents
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{COMMERCIAL_DOCUMENT_LABELS[draft.kind]}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {draft.customerName} · {state.filing ? "Signed and filed" : "Internal draft"}
            {record ? ` · Saved version ${record.revision}` : ""}
          </p>
        </div>
        {record && state.filing ? <FiledNotice record={record} filing={state.filing} /> : null}
      </header>
      <EditorFooter state={state} props={props} />
      <EditorTabs state={state} canWrite={state.editable} />
      <EditorBody state={state} props={props} />
      {state.filingOpen && record ? (
        <FileSignedDialog
          slug={props.slug}
          record={record}
          onClose={() => {
            state.setFilingOpen(false);
          }}
          onFiled={(next) => {
            state.filed(record, next);
          }}
        />
      ) : null}
    </section>
  );
}

function EditorTabs({ state, canWrite }: { state: EditorState; canWrite: boolean }) {
  const { tab, setTab, record, busy } = state;
  return (
    <>
      <nav aria-label="Document views" className="flex flex-wrap gap-2 border-b pb-3">
        {(canWrite ? (["edit", "history"] as const) : (["preview", "history"] as const))
          .filter((view) => view !== "history" || record)
          .map((view) => (
            <Button
              key={view}
              variant={tab === view ? "secondary" : "ghost"}
              disabled={busy}
              onClick={() => {
                setTab(view);
              }}
            >
              {view === "edit" ? "Edit" : view === "preview" ? "Preview" : "History"}
            </Button>
          ))}
      </nav>
    </>
  );
}
function EditorBody({ state, props }: { state: EditorState; props: EditorProps }) {
  const { tab, busy, draft, record, dirty, saved, editable } = state;
  return (
    <>
      {tab !== "history" ? (
        <div
          className={
            editable
              ? "grid items-start gap-8 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]"
              : "mx-auto max-w-3xl"
          }
        >
          {editable ? <EditorFields state={state} /> : null}
          <aside
            aria-label="Live preview"
            className="min-w-0 space-y-3 xl:sticky xl:top-6 xl:max-h-[calc(100dvh-3rem)] xl:overflow-y-auto"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-base font-semibold">Preview</h2>
              {editable ? (
                <p className="text-xs text-muted-foreground">Updates as you type</p>
              ) : null}
            </div>
            <CommercialDocumentPreview document={draft} />
          </aside>
        </div>
      ) : null}
      {tab === "history" && record ? (
        <CommercialDocumentHistory
          key={record.revision}
          slug={props.slug}
          current={record}
          canWrite={editable && !dirty && !busy}
          onRestored={saved}
          onBusy={state.setHistoryBusy}
        />
      ) : null}
      {tab === "history" && dirty ? (
        <p className="text-sm text-muted-foreground">Save your edits before restoring a version.</p>
      ) : null}
    </>
  );
}
function EditorFields({ state }: { state: EditorState }) {
  const { busy, draft, setDraft, changeNote, setChangeNote } = state;
  return (
    <fieldset aria-label="Document fields" disabled={busy} className="min-w-0 space-y-5 pb-8">
      <CommercialDocumentFields value={draft} onChange={setDraft} />
      <div>
        <FieldLabel htmlFor="commercial-change-note">Change summary (optional)</FieldLabel>
        <Input
          id="commercial-change-note"
          value={changeNote}
          maxLength={500}
          onChange={(event) => {
            setChangeNote(event.target.value);
          }}
        />
      </div>
    </fieldset>
  );
}
function EditorFooter({ state, props }: { state: EditorState; props: EditorProps }) {
  const { validation, save, download, busy, dirty, record, editable } = state;
  return (
    <>
      {validation.length ? (
        <FieldError>
          <ul>
            {validation.map((message, i) => (
              <li key={`${i}-${message}`}>{message}</li>
            ))}
          </ul>
        </FieldError>
      ) : null}
      {save.error ? <FieldError>{save.error.message}</FieldError> : null}
      {download.error ? <FieldError>{download.error.message}</FieldError> : null}
      <footer className="flex flex-wrap items-center gap-2 rounded-md border bg-card p-3">
        <p role="status" className="mr-auto text-xs text-muted-foreground">
          {save.isPending
            ? "Saving…"
            : dirty
              ? "Unsaved changes"
              : state.filing
                ? `Signed version ${state.filing.revision}`
                : record
                  ? "All changes saved"
                  : "Not saved yet"}
        </p>
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => {
            download.mutate();
          }}
        >
          {record && !dirty ? "Download PDF" : "Download draft PDF"}
        </Button>
        <RecordActions state={state} props={props} />
        {editable ? (
          <>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => {
                save.mutate(false);
              }}
            >
              Save document
            </Button>
            <Button
              disabled={busy}
              onClick={() => {
                save.mutate(true);
              }}
            >
              Save & download PDF
            </Button>
          </>
        ) : null}
      </footer>
    </>
  );
}
function RecordActions({ state, props }: { state: EditorState; props: EditorProps }) {
  const { busy, dirty, record, editable, filing } = state;
  if (!props.canWrite || !record || dirty) return null;
  return (
    <>
      <DeleteDocument
        slug={props.slug}
        source="commercial"
        id={record.id}
        revision={record.revision}
        title={record.title}
        disabled={busy}
        onDeleted={props.onClose}
      />
      <Button
        variant="ghost"
        disabled={busy}
        onClick={() => {
          props.onDuplicate(record);
        }}
      >
        <Copy /> Duplicate
      </Button>
      {editable && !filing ? (
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => {
            state.setFilingOpen(true);
          }}
        >
          <FileCheck /> File signed copy
        </Button>
      ) : null}
    </>
  );
}
